import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import { api } from "./client";

export type Tier = "starter" | "professional" | "enterprise";
export type LicenseStatus = "trial" | "active" | "expired" | "suspended";

export interface LicenseResponse {
  id: string;
  tenant_id: string;
  license_key: string;
  tier: Tier;
  max_cameras: number;
  max_sites: number;
  max_users: number;
  features: Record<string, boolean>;
  issued_at: string;
  expires_at?: string | null;
  activated_at?: string | null;
  status: LicenseStatus;
  created_at: string;
  updated_at: string;
  days_until_expiry?: number | null;
  // P5/license-state — the UI derives banner state from these, NOT the raw
  // `status` column (which can be stale, e.g. enterprise left at "trial").
  is_trial: boolean;
  lifecycle: "active" | "grace" | "expired";
}

export interface UsageResponse {
  tenant_id: string;
  tier?: Tier | null;
  status?: LicenseStatus | null;
  cameras: { current: number; max: number };
  sites: { current: number; max: number };
  users: { current: number; max: number };
  features: Record<string, boolean>;
  expires_at?: string | null;
  days_until_expiry?: number | null;
}

const licenseKey = ["license"] as const;

export function useLicense() {
  return useQuery({
    queryKey: [...licenseKey, "current"],
    queryFn: async () => {
      const { data } = await api.get<LicenseResponse | null>("/api/v1/license");
      return data;
    },
  });
}

export function useLicenseUsage() {
  return useQuery({
    queryKey: [...licenseKey, "usage"],
    queryFn: async () => {
      const { data } = await api.get<UsageResponse>("/api/v1/license/usage");
      return data;
    },
    refetchInterval: 60_000,
  });
}

export function useStartTrial() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (tier: Tier = "professional") => {
      const { data } = await api.post<LicenseResponse>(
        "/api/v1/license/trial",
        { tier },
      );
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: licenseKey });
    },
  });
}

export function useActivateLicense() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      license_key: string;
      tier: Tier;
      expires_at?: string;
    }) => {
      const { data } = await api.post<LicenseResponse>(
        "/api/v1/license/activate",
        input,
      );
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: licenseKey });
    },
  });
}
